import { useRef, type KeyboardEvent } from 'react'

import { cn } from '@/lib/utils'

/**
 * A tab strip.
 *
 * Radix is not reached for here because the behaviour that matters is small and specific: a
 * roving tab stop so the whole strip is one stop in the page's tab order, arrow keys to move
 * between panels, and Home and End to jump to the ends. Selecting a tab moves the panel
 * immediately — there is nothing to load, so a manual-activation model would only add a keystroke.
 */

export interface TabDefinition<TId extends string> {
  id: TId
  label: string
  /** Shown after the label, for a count or a marker. */
  badge?: string | number
}

export interface TabStripProps<TId extends string> {
  tabs: TabDefinition<TId>[]
  active: TId
  onChange: (id: TId) => void
  /** The id of the panel element the strip controls, for `aria-controls`. */
  panelId: string
  /** Names the strip for a screen reader — a record has sections, and this says whose. */
  label: string
  className?: string
}

export function TabStrip<TId extends string>({
  tabs,
  active,
  onChange,
  panelId,
  label,
  className,
}: TabStripProps<TId>) {
  const stripRef = useRef<HTMLDivElement | null>(null)

  const focusTab = (index: number) => {
    const buttons = Array.from(stripRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])
    const clamped = (index + buttons.length) % buttons.length
    buttons[clamped]?.focus()
    const tab = tabs[clamped]
    if (tab) {
      onChange(tab.id)
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === active)

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        focusTab(index + 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        focusTab(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusTab(0)
        break
      case 'End':
        event.preventDefault()
        focusTab(tabs.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      ref={stripRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      // Below `md` the strip scrolls sideways instead of wrapping. Six tabs wrapped across three
      // lines on a 375px screen would push the banner under it off the top of the screen, and on a
      // customer record that banner is the one thing that has to be read before anything else.
      className={cn(
        'flex items-center gap-1 overflow-x-auto border-b border-border-subtle md:flex-wrap',
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${panelId}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative -mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              'max-md:min-h-11',
              selected
                ? 'border-brand-fill text-heading'
                : 'border-transparent text-muted hover:border-border-strong hover:text-body',
            )}
          >
            {tab.label}
            {tab.badge === undefined ? null : (
              <span className="numeric rounded-full bg-neutral-subtle px-1.5 text-2xs text-body tabular-nums">
                {tab.badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
